<template>
    <div
        :id="`${id}-input-address`"
        class="tl-input-address"
        :data-testid="testId || 'inputaddress-wrapper'"
    >
        <Words
            v-if="label"
            class="tl-input-address-label"
        >{{ label }}</Words>

        <InputCountry
            :id="id"
            :initial-value="selectedCountry"
            :items="items"
            :selected-items="defaultCountry ? [defaultCountry] : undefined"
            :label="t('input-address.country')"
            :disabled="disabled"
            :focus="focus"
            display-country-flags
            block-type="INPUT_ADDRESS"
            @update:model-value="set('countryCode', $event?.countryCode ?? null)"
        />

        <InputAutocomplete
            v-if="autocompleteEnabled"
            :id="`${id}-search`"
            v-model="searchQuery"
            :label="t('search')"
            :disabled="disabled"
            :items="suggestions"
            :item-label="addressSuggestionLabel"
            :options-loading="suggestionsLoading"
            :no-results-eligible="searchCompleted"
            @search="handleSearchInput"
            @select="selectAddressSuggestion"
        />

        <InputText
            :model-value="value.addressLine1"
            :label="t('input-address.address-line-1')"
            :disabled="disabled"
            @update:model-value="set('addressLine1', $event)"
        />
        <InputText
            :model-value="value.addressLine2"
            :label="t('input-address.address-line-2')"
            :disabled="disabled"
            @update:model-value="set('addressLine2', $event)"
        />

        <InputText
            :model-value="value.postalCode"
            :label="t('input-address.postal-code')"
            :disabled="disabled"
            @update:model-value="set('postalCode', $event)"
        />

        <InputText
            :model-value="value.city"
            :label="t('input-address.city')"
            :disabled="disabled"
            @update:model-value="set('city', $event)"
        />

        <InputText
            :model-value="value.region"
            :label="t('input-address.region')"
            :disabled="disabled"
            @update:model-value="set('region', $event)"
        />

        <ErrorText
            v-if="error"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted } from 'vue'
import { useAddressSuggestions } from 'widget-components/composables/address-suggestions'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import InputAutocomplete from 'widget-components/components/InputAutocomplete/InputAutocomplete.vue'
import InputCountry from 'widget-components/components/InputCountry/InputCountry.vue'
import InputText from 'widget-components/components/InputText/InputText.vue'
import Words from 'widget-components/components/Words/Words.vue'
import { lazyBlockToolsInjectionKey } from 'widget-components/integrations/CustomPage/injection-keys'
import type { BlockPath, InputCountryItem, LazyBlockToolsInject } from 'widget-components/integrations/CustomPage/types'
import { useI18n } from 'widget-components/utilities/i18n'
import { addressSuggestionLabel, type AddressValue, emptyAddress } from './types'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    id: string
    initialValue?: AddressValue | null
    label?: string
    countries?: InputCountryItem[]
    disabled?: boolean
    focus?: boolean
    error?: string | boolean
    blockPath?: BlockPath
    testId?: string
    autocompleteEnabled?: boolean
    defaultCountry?: InputCountryItem | null
}>()

const { getLazyBlockProp } = inject(lazyBlockToolsInjectionKey, {
    requestBlockConfig: () => {},
    getLazyBlockProp: () => {},
    dispatchComponentAction: async () => {},
} as LazyBlockToolsInject)

const { t } = useI18n()

const model = defineModel<AddressValue | null>()

const value = computed(() => model.value ?? emptyAddress())

const items = computed(() => {
    const lazyItems = getLazyBlockProp<InputCountryItem[] | undefined>(props.id || 'no-id', 'items')
    return lazyItems?.length ? lazyItems : (props.countries || [])
})

const selectedCountry = computed<InputCountryItem | undefined>(() => {
    const countryCode = value.value.countryCode

    if (!countryCode) {
        return undefined
    }

    if (props.defaultCountry?.countryCode === countryCode) {
        return props.defaultCountry
    }

    // InputCountry resolves this against its own (lazily loaded) items list
    return { itemId: '', countryCode } as InputCountryItem
})

const {
    searchQuery,
    suggestions,
    suggestionsLoading,
    searchCompleted,
    handleSearchInput,
    clearSearch,
} = useAddressSuggestions<AddressValue>(() => props.id, () => value.value.countryCode)

const set = <K extends keyof AddressValue>(field: K, fieldValue: AddressValue[K]) => {
    model.value = { ...value.value, [field]: fieldValue }
}

const selectAddressSuggestion = (suggestion: AddressValue) => {
    model.value = { ...value.value, ...suggestion }
    clearSearch()
}

onMounted(() => {
    if (props.initialValue) {
        model.value = props.initialValue
    } else if (items.value.length === 1 && !value.value.countryCode) {
        set('countryCode', items.value[0].countryCode ?? null)
    }
})
</script>

<style scoped lang="sass">
.tl-input-address
    display: flex
    flex-direction: column
    gap: var(--tl-input-address-gap, 16px)

    &-label
        display: block
        margin-top: var(--tl-cp-block-margin)
</style>
